import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { CheckCircle, Warning, LinkBreak } from "@phosphor-icons/react";
import { sendFingerprintFireAndForget } from "../lib/browserFingerprint";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const API_ROOT = process.env.REACT_APP_BACKEND_URL;

export default function PublicTestPage({ testType }) {
  const { testLink } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linkError, setLinkError] = useState(null); // {status, detail} when fetch fails
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitProgress, setSubmitProgress] = useState(0);
  const [submitStep, setSubmitStep] = useState("Salvataggio risposte...");
  const [submitError, setSubmitError] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const progressTimerRef = useRef(null);

  useEffect(() => {
    const endpoint = testType === "employee" ? `${API}/test/employee/${testLink}` : `${API}/test/candidate/${testLink}`;
    axios.get(endpoint).then(r => {
      // Sanity check: backend must return questions for an active test
      if (!r.data || (!r.data.completed && (!Array.isArray(r.data.questions) || r.data.questions.length === 0))) {
        setLinkError({ status: 200, detail: "Risposta del server non valida" });
        return;
      }
      setData(r.data);
      if (r.data.completed) setCompleted(true);
      if (r.data.progress) { setAnswers(r.data.progress); setCurrentQ(Object.keys(r.data.progress).length); }
    }).catch((err) => {
      const status = err?.response?.status || 0;
      const detail = err?.response?.data?.detail || (status === 404 ? "Link non valido o scaduto" : "Errore di connessione");
      setLinkError({ status, detail });
    }).finally(() => setLoading(false));
  }, [testLink, testType]);

  // B73 Task 3.1 — fingerprint browser NON-BLOCCANTE (fire-and-forget).
  // Parte SOLO dopo che il test è stato caricato con successo (data disponibile,
  // non completato, no errori). Nessun `await`, tutto in try/catch nel modulo.
  // Se canvas/WebGL sono bloccati (privacy mode, estensioni), collectFingerprintSafe
  // ritorna null → nessuna POST → l'utente non nota nulla.
  useEffect(() => {
    if (!data || completed || linkError) return;
    try {
      sendFingerprintFireAndForget(API_ROOT, testLink);
    } catch { /* impossibile ma sicurezza */ }
  }, [data, completed, linkError, testLink]);

  const questions = data?.questions || [];
  const progress = questions.length > 0 ? Math.round((Object.keys(answers).length / questions.length) * 100) : 0;

  const selectAnswer = (qId, optIdx) => {
    const newAnswers = { ...answers, [qId]: optIdx };
    setAnswers(newAnswers);
    // Auto-save every 6 questions
    if (Object.keys(newAnswers).length % 6 === 0) {
      const endpoint = testType === "employee" ? `${API}/test/employee/${testLink}/submit` : `${API}/test/candidate/${testLink}/submit`;
      axios.post(endpoint, { answers: newAnswers, is_final: false }).catch(() => {});
    }
    // Auto-advance after short delay
    setTimeout(() => { if (currentQ < questions.length - 1) setCurrentQ(currentQ + 1); }, 300);
  };

  const startProgressAnimation = () => {
    setSubmitProgress(5);
    setSubmitStep("Salvataggio risposte...");
    // Simulated progression: ramps up steadily, caps at 92% until server returns
    progressTimerRef.current = setInterval(() => {
      setSubmitProgress((p) => {
        if (p >= 92) return 92;
        // slower as it gets higher
        const inc = p < 40 ? 6 : p < 70 ? 3 : 1;
        const next = Math.min(92, p + inc);
        if (next > 35 && next <= 70) setSubmitStep("Calcolo punteggi...");
        else if (next > 70) setSubmitStep("Invio in corso...");
        return next;
      });
    }, 400);
  };

  const stopProgressAnimation = () => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  };

  const submitTest = async () => {
    setSubmitting(true);
    setSubmitError(null);
    startProgressAnimation();
    const endpoint = testType === "employee" ? `${API}/test/employee/${testLink}/submit` : `${API}/test/candidate/${testLink}/submit`;
    // C20: idempotency key client-side per evitare doppi submit lato server
    const idempotencyKey = (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${testLink}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const headers = { "Idempotency-Key": idempotencyKey };
    const maxAttempts = 3; // 1 originale + 2 retry
    const backoffMs = [0, 2000, 5000];
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        if (attempt > 1) {
          setSubmitStep(`Riprovo… (${attempt}/${maxAttempts})`);
          await new Promise(r => setTimeout(r, backoffMs[attempt - 1]));
        }
        const res = await axios.post(endpoint, { answers, is_final: true }, { timeout: 60000, headers });
        stopProgressAnimation();
        setSubmitProgress(100);
        setSubmitStep("Completato");
        setTimeout(() => setCompleted(true), 400);
        return res;
      } catch (e) {
        const status = e.response?.status;
        const isNetworkError = !e.response;
        const isRetryable = isNetworkError || (status >= 500 && status <= 599);
        const isLastAttempt = attempt === maxAttempts;
        // Detailed logging
        const detail = e.response?.data?.detail || e.response?.data?.message || e.message;
        console.error(`Submit attempt ${attempt}/${maxAttempts} failed:`, status, detail, e);
        if (!isRetryable || isLastAttempt) {
          stopProgressAnimation();
          setSubmitError(`${status ? `Errore ${status}: ` : ""}${detail || "Errore di rete"}`);
          setSubmitting(false);
          return;
        }
        // continua loop per retry
      }
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen bg-[#F4F7FB]"><div className="animate-spin w-8 h-8 border-2 border-[#1B4FD8] border-t-transparent rounded-full" /></div>;

  // Bug-fix: link invalido / candidato eliminato / server errore — mostra messaggio chiaro invece di pagina bianca
  if (linkError) return (
    <div className="flex items-center justify-center min-h-screen bg-[#F4F7FB]" data-testid="link-error-page">
      <Card className="max-w-md w-full mx-4 border-amber-200">
        <CardContent className="p-8 text-center">
          <LinkBreak size={48} weight="duotone" className="text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-[#0D1F3C] mb-2">
            {linkError.status === 404 ? "Link non valido o scaduto" : "Impossibile caricare l'analisi"}
          </h2>
          <p className="text-sm text-[#0D1F3C]/70 mb-4" data-testid="link-error-detail">
            {linkError.status === 404
              ? "Il link che hai usato non risulta attivo. Potrebbe essere stato rimosso o scaduto. Contatta il referente che ti ha invitato per ricevere un nuovo link."
              : linkError.detail}
          </p>
          <p className="text-xs text-[#0D1F3C]/40">Codice: {linkError.status || "ERR"}</p>
        </CardContent>
      </Card>
    </div>
  );

  if (submitting) return (
    <div className="flex items-center justify-center min-h-screen bg-[#F4F7FB]">
      <Card className="max-w-md w-full mx-4 border-[#0D1F3C]/8">
        <CardContent className="p-8 text-center">
          <div className="animate-spin w-10 h-10 border-3 border-[#1B4FD8] border-t-transparent rounded-full mx-auto mb-5" data-testid="submit-spinner" />
          <h2 className="text-lg font-semibold text-[#0D1F3C] mb-2">Invio in corso...</h2>
          <p className="text-sm text-[#0D1F3C]/60 mb-1" data-testid="submit-step">{submitStep}</p>
          <p className="text-xs text-[#0D1F3C]/40 mb-4">Non chiudere questa pagina.</p>
          <Progress value={submitProgress} className="h-2" data-testid="submit-progress" />
        </CardContent>
      </Card>
    </div>
  );
  if (submitError) return (
    <div className="flex items-center justify-center min-h-screen bg-[#F4F7FB]">
      <Card className="max-w-md w-full mx-4 border-red-200">
        <CardContent className="p-8 text-center">
          <Warning size={48} weight="duotone" className="text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-[#0D1F3C] mb-2">Invio non riuscito</h2>
          <p className="text-sm text-[#0D1F3C]/70 mb-4" data-testid="submit-error">{submitError}</p>
          <p className="text-xs text-[#0D1F3C]/40 mb-5">Le tue risposte sono salvate: puoi riprovare in sicurezza.</p>
          <Button onClick={() => { setSubmitError(null); submitTest(); }} data-testid="submit-retry-btn" className="bg-[#1B4FD8] hover:bg-[#0D1F3C] w-full">
            Riprova invio
          </Button>
        </CardContent>
      </Card>
    </div>
  );
  if (completed) return (
    <div className="flex items-center justify-center min-h-screen bg-[#F4F7FB]">
      <Card className="max-w-md w-full mx-4 border-[#0D1F3C]/8">
        <CardContent className="p-8 text-center">
          <CheckCircle size={48} weight="duotone" className="text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-[#0D1F3C] mb-2">Grazie!</h2>
          <p className="text-sm text-[#0D1F3C]/60">Abbiamo ricevuto la tua valutazione. Ti contatteremo nei prossimi giorni.</p>
        </CardContent>
      </Card>
    </div>
  );

  if (showIntro) return (
    <div className="flex items-center justify-center min-h-screen bg-[#F4F7FB]">
      <Card className="max-w-md w-full mx-4 border-[#0D1F3C]/8">
        <CardContent className="p-8">
          <h1 className="text-2xl font-bold text-[#0D1F3C] mb-2">SELLY<span className="text-[#56A3F5]">®</span></h1>
          <h2 className="text-lg font-medium text-[#0D1F3C] mb-4">Valutazione Comportamentale</h2>
          <div className="bg-[#56A3F5]/5 border border-[#56A3F5]/20 rounded-lg p-4 mb-6">
            <p className="text-sm text-[#0D1F3C]/80 leading-relaxed">
              Prenditi 20 minuti in un posto tranquillo. Potrai interromperti e riprendere, ma non sara possibile modificare le risposte gia date ne ripetere l'analisi.
            </p>
          </div>
          <p className="text-sm text-[#0D1F3C]/50 mb-4">{questions.length} domande &middot; Circa 20 minuti</p>
          <Button onClick={() => setShowIntro(false)} data-testid="start-test-btn" className="w-full bg-[#1B4FD8] hover:bg-[#0D1F3C] h-12 text-base">
            Ho capito, inizia la valutazione
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  const q = questions[currentQ];
  if (!q) return null;

  const isLastQuestion = currentQ === questions.length - 1;
  const currentHasAnswer = answers[q.question_id] !== undefined;

  return (
    <div className="min-h-screen bg-[#F4F7FB] flex flex-col" data-testid="test-page">
      <header className="bg-white border-b border-[#0D1F3C]/8 px-6 py-3 flex items-center justify-between">
        <h1 className="text-lg font-bold text-[#0D1F3C]">SELLY<span className="text-[#56A3F5]">®</span></h1>
        <div className="flex items-center gap-4">
          <span className="text-sm text-[#0D1F3C]/50">{Object.keys(answers).length}/{questions.length}</span>
          <div className="w-40"><Progress value={progress} className="h-2" /></div>
        </div>
      </header>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-2xl w-full">
          <div className="mb-4 flex items-center gap-3">
            <span className="text-xs font-medium text-[#0D1F3C]/40 uppercase tracking-wider">Domanda {currentQ + 1} di {questions.length}</span>
            <span className="text-xs text-[#0D1F3C]/30">Blocco {q.block}</span>
          </div>
          <h2 className="text-lg font-medium text-[#0D1F3C] mb-6 leading-relaxed">{q.text}</h2>
          <div className="space-y-3">
            {q.options.map((opt, idx) => (
              <button key={idx} onClick={() => selectAnswer(q.question_id, idx)}
                data-testid={`option-${idx}`}
                className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                  answers[q.question_id] === idx
                    ? 'border-[#1B4FD8] bg-[#1B4FD8]/5 shadow-md'
                    : 'border-[#0D1F3C]/10 hover:border-[#1B4FD8]/30 hover:bg-white'
                }`}>
                <span className="text-sm text-[#0D1F3C]/80">{opt.text}</span>
              </button>
            ))}
          </div>
          <div className="flex items-center justify-between mt-8">
            <Button variant="outline" onClick={() => setCurrentQ(Math.max(0, currentQ - 1))} disabled={currentQ === 0}
              className="border-[#0D1F3C]/15" data-testid="prev-question-btn">Precedente</Button>
            {isLastQuestion ? (
              <Button onClick={submitTest} disabled={submitting || !currentHasAnswer} data-testid="submit-test-btn"
                className="bg-[#1B4FD8] hover:bg-[#0D1F3C] px-8 disabled:opacity-50">{submitting ? "Invio..." : "Completa Analisi"}</Button>
            ) : (
              <Button onClick={() => setCurrentQ(currentQ + 1)} disabled={!currentHasAnswer} data-testid="next-question-btn"
                className="bg-[#1B4FD8] hover:bg-[#0D1F3C] disabled:opacity-50">Successiva</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
